import React, { useEffect, useState } from "react";
import { Box, Grid, Stack, Typography, Alert } from "@mui/material";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { Surface, SectionTitle, Kpi, StatusChip } from "components/ui";
import { T, inr, inrShort, statusTone } from "theme/tokens";
import * as api from "../api";

function Field({ label, value, tone }) {
  return (
    <Box sx={{ mb: 1.4 }}>
      <Typography
        sx={{
          fontSize: "0.65rem",
          fontWeight: 600,
          color: T.subtle,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: "0.88rem", color: tone || T.ink, wordBreak: "break-word" }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function OverviewTab({ partnerId, partner }) {
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([api.getStats(partnerId), api.getWallet(partnerId)])
      .then(([statsRes, walletRes]) => {
        if (!alive) return;
        setStats(statsRes.stats);
        setWallet(walletRes);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.response?.data?.message || "Could not load statistics.");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [partnerId]);

  const trend = (stats?.trend || []).map((d) => ({ date: d._id, count: d.count }));
  const statuses = Object.entries(stats?.statusCounts || {}).sort(
    (a, b) => b[1] - a[1]
  );

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      {/* ── KPIs ───────────────────────────────────────────────────────── */}
      <Surface>
        <Grid container>
          {[
            { label: "Deliveries", value: stats?.totalOrders ?? 0 },
            {
              label: "Order value carried",
              value: inrShort(stats?.orderValue),
              sub: `${stats?.paidOrders ?? 0} paid`,
              tone: T.info,
            },
            {
              label: "Wallet",
              value: inr(wallet?.balance),
              sub: wallet?.hasBankDetails ? "bank on file" : "no bank on file",
              tone: wallet?.balance > 0 ? T.warning : T.muted,
            },
            {
              label: "Last 30 days",
              value: trend.reduce((sum, d) => sum + d.count, 0),
              sub: "deliveries",
              tone: T.success,
            },
          ].map((kpi, index) => (
            <Grid
              item
              xs={6}
              md={3}
              key={kpi.label}
              sx={{
                borderRight: { md: index < 3 ? `1px solid ${T.borderSoft}` : "none" },
                borderBottom: { xs: `1px solid ${T.borderSoft}`, md: "none" },
              }}
            >
              <Kpi {...kpi} loading={loading} />
            </Grid>
          ))}
        </Grid>
      </Surface>

      <Grid container spacing={2}>
        {/* ── Who they are ─────────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Surface sx={{ p: 2, height: "100%" }}>
            <SectionTitle>Partner</SectionTitle>
            <Field label="Name" value={partner?.fullname} />
            <Field label="Phone (login)" value={partner?.phone} />
            <Field label="Vehicle" value={partner?.vehicleNumber} />
            <Field label="Driving licence" value={partner?.drivingLicense} />
            <Field
              label="Joined"
              value={
                partner?.date
                  ? new Date(partner.date).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : null
              }
            />
            <Field
              label="Last known position"
              value={
                partner?.coordinates
                  ? `${partner.coordinates.lat.toFixed(5)}, ${partner.coordinates.lng.toFixed(5)}`
                  : "Never reported"
              }
              tone={partner?.coordinates ? T.ink : T.subtle}
            />
          </Surface>
        </Grid>

        {/* ── Readiness ────────────────────────────────────────────────── */}
        <Grid item xs={12} md={8}>
          <Surface sx={{ p: 2, height: "100%" }}>
            <SectionTitle>Can they take a job right now?</SectionTitle>
            {/* Three separate conditions, all of which must hold. Showing them
                as one "active/inactive" badge is what made the old screens
                impossible to act on: you could not see which one was missing. */}
            <Stack spacing={1.2} sx={{ mt: 1 }}>
              {[
                {
                  label: "Approved by Medstown",
                  ok: Boolean(partner?.isVerified),
                  fix: "Approve them on this page.",
                },
                {
                  label: "Signed in on the partner app",
                  ok: Boolean(partner?.loginStatus),
                  fix: "Only they can sign in — the app controls this.",
                },
                {
                  label: "Accepting deliveries",
                  ok: Boolean(partner?.isTakingOrders),
                  fix: "Resume order-taking on this page.",
                },
                {
                  label: "KYC documents uploaded",
                  ok: Boolean(partner?.hasDocuments),
                  fix: "They upload these from the app.",
                  advisory: true,
                },
                {
                  label: "Bank details on file",
                  ok: Boolean(partner?.hasBankDetails),
                  fix: "Needed before they can be paid, not before they can work.",
                  advisory: true,
                },
              ].map((row) => (
                <Stack
                  key={row.label}
                  direction="row"
                  alignItems="center"
                  spacing={1.2}
                  sx={{
                    py: 1,
                    px: 1.2,
                    borderRadius: 2,
                    border: `1px solid ${T.borderSoft}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      flexShrink: 0,
                      bgcolor: row.ok
                        ? T.success
                        : row.advisory
                        ? T.warning
                        : T.error,
                    }}
                  />
                  <Typography sx={{ fontSize: "0.85rem", color: T.ink, fontWeight: 600 }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: T.subtle, ml: "auto" }}>
                    {row.ok ? "Yes" : row.fix}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Surface>
        </Grid>
      </Grid>

      {/* ── Delivery trend ─────────────────────────────────────────────── */}
      <Surface sx={{ p: 2 }}>
        <SectionTitle>Deliveries, last 30 days</SectionTitle>
        {trend.length ? (
          <Box sx={{ height: 200, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="deliveryTrend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={T.secondary} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={T.secondary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.borderSoft} vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: T.subtle }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => String(v).slice(5)}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: T.subtle }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <RTooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: `1px solid ${T.border}`,
                    fontSize: "0.8rem",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={T.secondary}
                  strokeWidth={2}
                  fill="url(#deliveryTrend)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "0.85rem", color: T.subtle, py: 3, textAlign: "center" }}>
            {loading ? "Loading…" : "No deliveries in the last 30 days."}
          </Typography>
        )}
      </Surface>

      {/* ── Status mix ─────────────────────────────────────────────────── */}
      {statuses.length > 0 && (
        <Surface sx={{ p: 2 }}>
          <SectionTitle>Every delivery by status</SectionTitle>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {statuses.map(([status, count]) => (
              <StatusChip
                key={status}
                status={status}
                label={`${status} · ${count}`}
                tone={statusTone(status)}
              />
            ))}
          </Stack>
        </Surface>
      )}
    </Stack>
  );
}
